import { Box } from '@everlywell/ui-kit';
import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

interface StickyFooterProps {
  type?: 'fixed' | 'sticky' | 'inline';
  // The background color of the sticky footer. Defaults to 'tints.lightCream'.
  backgroundColorOverride?: string;
  // The content to be displayed in the sticky footer.
  children: React.ReactNode;
}

/**
 * The component becomes sticky when the viewport width is less than or equal to the specified breakpoint.
 * @example
 * // The component will stick when the viewport width is less than or equal to 600px (tablet breakpoint).
 * <StickyMobileFooter breakpoint={theme.breakpoints.tablet}>>
 *   {children}
 * </StickyMobileFooter>
 */
const StickyFooter = ({
  children,
  type = 'sticky',
  backgroundColorOverride = 'tints.lightCream',
}: StickyFooterProps) => {
  const [inViewSensorRef, inView] = useInView({
    skip: type !== 'sticky',
  });

  let position: 'unset' | Exclude<StickyFooterProps['type'], 'inline'> =
    type === 'inline' ? 'unset' : type;

  const [elementHeight, setElementHeight] = React.useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set the height of the spacer element to the height of the container when the type is fixed.
    if (containerRef.current && type === 'fixed') {
      setElementHeight(containerRef.current.offsetHeight);
    } else {
      setElementHeight(0);
    }
  }, [type]);

  const floatingStyles = {
    bg: [!inView ? backgroundColorOverride : '0 none', null, 'none'],
    borderTopRadius: ['3xl', null, 0],
    boxShadow: [!inView ? 'dialog' : 'none', null, 'none'],
    bottom: '0',
    left: '0',
  };

  return (
    <>
      <Box
        ref={containerRef}
        display={'flex'}
        sx={{
          ...(type !== 'inline' && floatingStyles),
        }}
        position={[position, null, 'unset']}
        width={!inView ? '100%' : 'auto'}
        zIndex={'docked'}
        flexDirection="column"
        alignItems="center"
      >
        {children}
      </Box>
      <Box ref={inViewSensorRef} height={elementHeight} />
    </>
  );
};

export default StickyFooter;
